import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import SummaryDefinitionsService from 'services/summaryDefinitions';
import { throwError } from 'utilities';
import {
  SUMMARY_DEFINITIONS_QUERY_KEYS,
  SummaryDefinitionListResponse,
  UseGetDefinitionsReturn,
  UseGetFirmValuationsDefinitionsParams,
} from './types';

const DEFAULT_ERROR_MESSAGE = 'Failed to fetch firm valuations definitions';

export const getFirmValuationsDefinitions = async (
  firmId: number
): Promise<SummaryDefinitionListResponse | null> => {
  if (isNil(firmId)) {
    return null;
  }

  const service = new SummaryDefinitionsService();

  try {
    const response = await service.getFirmValuationsDefinitions(firmId);
    if (!response) {
      return throwError(DEFAULT_ERROR_MESSAGE);
    }
    return response;
  } catch (error) {
    return throwError(DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmValuationsDefinitions = (
  params: UseGetFirmValuationsDefinitionsParams
): UseGetDefinitionsReturn => {
  const { firmId, enabled = true } = params;

  const queryFn = useCallback(() => getFirmValuationsDefinitions(firmId), [firmId]);

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    SummaryDefinitionListResponse | null,
    Error
  >({
    enabled: !isNil(firmId) && enabled,
    queryFn,
    queryKey: SUMMARY_DEFINITIONS_QUERY_KEYS.valuations(firmId),
    staleTime: 30000,
  });

  return {
    data: data ?? undefined,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmValuationsDefinitions;
